import { SelectOptionType } from '@octano/global-ui';
import {
  CreateCourseBody,
  UpdateCourseAdministrativeBody,
  UpdateCourseTeachingBody,
} from '../../../types/courseMaintainerTypes';
import { getSelectedOption } from '../../../utils/selectFormat';
import {
  Course,
  CourseFormData,
  CourseFormFields,
  CourseSyllabus,
  RetakeAllowedCodes,
  CourseFormMode,
} from './types';

/**
 * ISS-05 — Coacciona un valor de formulario a número (o `null`).
 *
 * `onlineAttendancePercentage` se inicializa desde la respuesta GET, donde
 * Postgres devuelve columnas numéricas como strings. Si el usuario no edita el
 * campo, el string llega al payload y el backend (`@IsNumber()`) responde
 * HTTP 400.
 *
 * `0` es un valor válido, por lo que NO se usa un chequeo truthy: se filtra
 * explícitamente `null`/`undefined`/string vacío.
 */
const toNumberOrNull = (
  value: number | string | null | undefined,
): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (((value as unknown) as string) === '') {
    return null;
  }
  return +value;
};

export const syllabusToFile = (
  syllabus?: CourseSyllabus | null,
): File | null => {
  if (!syllabus) {
    return null;
  }
  return new File([] as any, `${syllabus?.name}.${syllabus?.extension}`, {
    lastModified: -2,
  });
};

export const baseToSelectOption = <T extends { id: number; name: string }>(
  items?: T[],
): SelectOptionType[] =>
  items?.map((e) => ({
    label: e.name,
    value: e.id,
  })) || [];

export const courseToFormFields = (
  course: Course,
  {
    schools = [],
    academicLevels = [],
    modalities = [],
    replaceCourseGradeOptions = [],
    courseTypes = [],
  }: CourseFormData,
): CourseFormFields => {
  const defaultCourseTypeId = courseTypes[0]?.value
    ? [+courseTypes[0].value]
    : [];
  const isCreditsNotApplicable = course.credits <= 0;
  return {
    // CourseDetailsSection
    shortening: course.shortening,
    code: course.code,
    name: course.name,
    credits: isCreditsNotApplicable ? '0' : course.credits.toString(),
    isCreditsNotApplicable,
    totalHours: course.totalHours?.toString() ?? '',
    presentialPedagogicalHours:
      course.presentialPedagogicalHours?.toString() ?? '',
    weeklyHours: course.weeklyHours?.toString() ?? '',
    school: getSelectedOption(course.schoolId, schools),
    academicLevel: getSelectedOption(course.academicLevelId, academicLevels),
    modality: getSelectedOption(course.modalityId ?? 0, modalities),
    replaceCourseGradeAllowed: getSelectedOption(
      course.replaceCourseGradeAllowed
        ? RetakeAllowedCodes.Enabled
        : RetakeAllowedCodes.Disabled,
      replaceCourseGradeOptions,
    ),
    replaceEvaluationGradeAllowed: getSelectedOption(
      course.replaceEvaluationGradeAllowed
        ? RetakeAllowedCodes.Enabled
        : RetakeAllowedCodes.Disabled,
      replaceCourseGradeOptions,
    ),
    isNonStructuredAvailable: course.isNonStructuredAvailable
      ? { label: 'Si', value: 1 }
      : { label: 'No', value: 0 },
    // Course type defaults to the first available (Obligatorio); section UI removed.
    courseTypes: defaultCourseTypeId,
    // CourseActivityTypeSection,
    attendance:
      course?.assistanceRequirements?.map((e) => ({
        minPercentage: e.minPercentage,
        activityTypeId: e.activityTypeId,
        totalHours: e.totalHours ?? null,
        weeklyHours: e.weeklyHours ?? null,
      })) ?? [],
    onlineAttendancePercentage: course?.onlineAttendancePercentage ?? null,
    // HU 15746 — Horas del componente online persistidas a nivel de
    // asignatura (paralelas a `onlineAttendancePercentage`).
    onlineTotalHours: course?.onlineTotalHours ?? null,
    onlineWeeklyHours: course?.onlineWeeklyHours ?? null,
    // CourseSyllabusSection
    syllabusFile: syllabusToFile(course?.syllabus),
    // CourseCompatibilitiesSection
    compatibilities:
      course?.compatibilities?.map((e) => ({
        id: e.id,
        shortening: e.shortening,
        name: e.name,
        credits: e.credits,
      })) ?? [],
    // CoursePrerequisitesSection
    prerequisites:
      course?.dependsOn?.map((e) => ({
        id: e.id,
        shortening: e.shortening,
        name: e.name,
        credits: e.credits,
      })) ?? [],
    learningResults:
      course.courseLearningResults?.map((e, index) => ({
        id: e.id,
        code: e.code,
        description: e.description,
      })) ?? [],
    learningUnits:
      course.courseLearningUnits?.map((e, index) => ({
        id: e.id,
        title: e.title,
        hours: e.hours,
      })) ?? [],
    // CourseGroupingsSection
    courseGroups: course.courseGroups ?? [],
  };
};

export const submitRequiresConfirmation = (
  values: CourseFormFields,
  mode: CourseFormMode,
): boolean => {
  return (
    !values.prerequisites ||
    (values.prerequisites.length === 0 && mode === CourseFormMode.CREATE)
  );
};

export const creationPayload = (
  values: CourseFormFields,
): CreateCourseBody => ({
  name: values.name,
  code: values.code,
  shortening: values.shortening,
  credits: Number.isFinite(Number(values?.credits))
    ? Number(values.credits)
    : 0,
  totalHours: values.totalHours ? +values.totalHours : null,
  presentialPedagogicalHours: values.presentialPedagogicalHours
    ? +values.presentialPedagogicalHours
    : null,
  weeklyHours: values.weeklyHours ? +values.weeklyHours : null,
  types: values?.courseTypes ?? [],
  modalityId: values.modality?.value ? +values.modality?.value : undefined,
  schoolId: values.school?.value ? +values.school?.value : undefined,
  academicLevelId: values.academicLevel?.value
    ? +values.academicLevel?.value
    : undefined,
  assistanceRequirements: values?.attendance ?? [],
  onlineAttendancePercentage: toNumberOrNull(
    values?.onlineAttendancePercentage,
  ),
  onlineTotalHours: toNumberOrNull(values?.onlineTotalHours),
  onlineWeeklyHours: toNumberOrNull(values?.onlineWeeklyHours),
  dependsOn:
    values?.prerequisites?.map((prerequisite) => prerequisite.id) ?? [],
  compatibilityIds:
    values?.compatibilities?.map((compatibility) => compatibility.id) ?? [],
  replaceCourseGradeAllowed:
    values.replaceCourseGradeAllowed?.value === RetakeAllowedCodes.Enabled,
  isNonStructuredAvailable: values.isNonStructuredAvailable?.value === 1,
  learningResults:
    values.learningResults?.map((lr) => ({
      code: lr.code!,
      description: lr.description!,
    })) ?? [],
  courseGroupIds: (values.courseGroups ?? []).map((g) => g.id),
});

/**
 * Construye el payload del endpoint PATCH :courseId/administrative.
 *
 * Cuando `isDisabled` es true el formulario oculta los campos básicos
 * (name/code/shortening/credits/presentialPedagogicalHours/school/academicLevel)
 * por lo que no se envían — el backend los trata como opcionales.
 *
 * HU 15447 - Task B13.
 */
export const updatingAdministrativePayload = (
  courseId: number,
  values: CourseFormFields,
  isDisabled: boolean,
): UpdateCourseAdministrativeBody => {
  const payload: UpdateCourseAdministrativeBody = {
    id: courseId,
    credits: Number.isFinite(Number(values?.credits))
      ? Number(values.credits)
      : 0,
    types: values.courseTypes ?? [],
    assistanceRequirements: values?.attendance ?? [],
    onlineAttendancePercentage: toNumberOrNull(
      values?.onlineAttendancePercentage,
    ),
    onlineTotalHours: toNumberOrNull(values?.onlineTotalHours),
    onlineWeeklyHours: toNumberOrNull(values?.onlineWeeklyHours),
    dependsOn:
      values?.prerequisites?.map((prerequisite) => prerequisite.id) ?? [],
    compatibilityIds:
      values?.compatibilities?.map((compatibility) => compatibility.id) ?? [],
    courseGroupIds: (values.courseGroups ?? []).map((g) => g.id),
    modalityId: values.modality?.value ? +values.modality?.value : undefined,
    totalHours: values.totalHours ? +values.totalHours : null,
    weeklyHours: values.weeklyHours ? +values.weeklyHours : null,
    replaceCourseGradeAllowed:
      values.replaceCourseGradeAllowed?.value === RetakeAllowedCodes.Enabled,
    replaceEvaluationGradeAllowed:
      values.replaceEvaluationGradeAllowed?.value ===
      RetakeAllowedCodes.Enabled,
    isNonStructuredAvailable: values.isNonStructuredAvailable?.value === 1,
  };
  if (!isDisabled) {
    return {
      ...payload,
      name: values.name,
      code: values.code,
      shortening: values.shortening,
      presentialPedagogicalHours: values.presentialPedagogicalHours
        ? +values.presentialPedagogicalHours
        : null,
      schoolId: values.school?.value ? +values.school?.value : undefined,
      academicLevelId: values.academicLevel?.value
        ? +values.academicLevel?.value
        : undefined,
    };
  }
  return payload;
};

/**
 * Construye el payload del endpoint PATCH :courseId/teaching.
 * Solo incluye learning units y learning results; el syllabus se sube en un
 * endpoint aparte (POST :courseId/syllabus).
 *
 * HU 15447 - Task B13.
 */
export const updatingTeachingPayload = (
  courseId: number,
  values: CourseFormFields,
): UpdateCourseTeachingBody => ({
  id: courseId,
  learningResults:
    values.learningResults?.map((lr) => ({
      id: lr.id!,
      code: lr.code!,
      description: lr.description!,
    })) ?? [],
  learningUnits:
    values.learningUnits?.map((lr) => ({
      id: lr.id!,
      title: lr.title!,
      hours: lr.hours!,
    })) ?? [],
});
